import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAssetTransactions, fetchUserAssetBalances, exchangeAsset, transferAssetP2P, requestAssetWithdrawal } from '../../../redux/WithcookieSlice';
import {
  AssetHistoryProps,
  AssetHistoryItem,
  AssetBalance,
  WithdrawFormState,
  ExchangeModalState,
  DepositModalState
} from './AssetHistory.types';
import { adaptAssetHistoryProps } from './AssetHistory.adapter';
import './AssetHistory.css';
import clsx from 'clsx';
import { useTranslation } from '../../../redux/TranslationHelper';
import { QRCodeSVG } from 'qrcode.react';

// 아이콘 컴포넌트들
const PointIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="16"></line>
    <line x1="8" y1="12" x2="16" y2="12"></line>
  </svg>
);

// 화살표 아이콘 컴포넌트
const ArrowDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

// 환전 아이콘 컴포넌트
const ExchangeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <path d="M8 12h8M12 16l4-4-4-4"></path>
  </svg>
);

// AssetIcon 컴포넌트 추가
const AssetIcon: React.FC<{iconUrl?: string, assetName?: string}> = ({ iconUrl, assetName = '' }) => {
  return (
    <div
      className="asset-icon-dollar"
      style={{
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        border: '1px solid #ddd',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa',
        marginRight: '8px',
        color: '#555',
        fontSize: '14px',
        fontWeight: 'bold'
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="16"></line>
        <line x1="8" y1="12" x2="16" y2="12"></line>
      </svg>
    </div>
  );
};

// 날짜 포맷 함수
const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

// 금액 포맷 함수
const formatAmount = (amount: number): string => {
  if (isNaN(amount)) return '0';
  return amount.toLocaleString();
};

// 1. P2P 가능 여부 함수 추가
const isP2PEnabled = (asset: any) => {
  if (!asset.p2p || !asset.p2p.enabled) return false;
  const companyIds = Object.keys(asset.companyDisplayNames || {});
  const companyId = companyIds.length > 0 ? Number(companyIds[0]) : null;
  if (!companyId || !asset.p2p.companySettings) return false;
  const companySetting = asset.p2p.companySettings.find((cs: any) => Number(cs.companyId) === companyId);
  return companySetting && companySetting.p2pEnabled === true;
};

// P2P 모달 상태 인터페이스
interface P2PModalState {
  showP2PModal: boolean;
  p2pTargetAsset: any;
  p2pToUserId: string;
  p2pAmount: string;
  p2pReason: string;
  p2pProcessing: boolean;
}

/**
 * 자산 내역 컴포넌트
 */
const AssetHistoryComponent: React.FC<any> = (props) => {
  // 어댑터를 통해 props 변환
  const adaptedProps = adaptAssetHistoryProps(props);

  // Redux 관련 설정
  const dispatch = useDispatch<any>();

  // 다국어 처리를 위한 훅 사용
  const { t } = useTranslation();

  // props에서 필요한 데이터 추출
  const {
    style = {},
    className = "mileage-history-wrapper padding-tb-40",
    isPreview = false,
    componentProps = {}
  } = adaptedProps;

  // 컴포넌트 props에서 제목과 빈 상태 메시지 추출 - 다국어 처리 적용
  const title = componentProps?.title ? t(componentProps.title) : t("자산 내역");
  const emptyText = componentProps?.emptyText ? t(componentProps.emptyText) : t("자산 내역이 없습니다.");

  // Redux 상태 조회
  const assetTransactions = useSelector((state: any) => state.withcookie?.assetTransactions);
  const assetBalances = useSelector((state: any) => state.withcookie?.userAssetBalances);
  const userInfo = useSelector((state: any) => state.withcookie?.userInfo || null);
  const withcookieData = useSelector((state: any) => state.withcookie?.withcookieData);

  // 여러 가능한 경로에서 회사 ID 찾기 시도
  const companyId = withcookieData?.skin?.company?.id ||
                    withcookieData?.company?.id;



  // 필요한 상태 값 추출 (useMemo 사용해 불필요한 재계산 방지)
  const assetTransactionsState = useMemo(() => {
    return {
      items: assetTransactions?.items || [],
      loading: assetTransactions?.loading || false,
      error: assetTransactions?.error || null,
      pagination: assetTransactions?.pagination || { page: 1, totalPages: 1, totalItems: 0 }
    };
  }, [assetTransactions]);

  const { items: transactionHistory, loading: transactionsLoading, error: transactionsError, pagination } = assetTransactionsState;

  const {
    balances,
    totalBalance,
    totalReceived,
    loading: balancesLoading,
    error: balancesError
  } = useMemo(() => {
    // 콘솔에 현재 assetBalances 상태 기록

    // assetBalances가 바로 배열인 경우 처리
    if (Array.isArray(assetBalances)) {
      return {
        balances: assetBalances,
        totalBalance: 0, // 여기서는 합산이 필요할 수 있음
        totalReceived: 0,
        loading: false,
        error: null
      };
    }

    // 객체 구조인 경우 처리
    return {
      balances: assetBalances?.balances || assetBalances?.assetBalances || [],
      totalBalance: assetBalances?.totalBalance || 0,
      totalReceived: assetBalances?.totalReceived || 0,
      loading: assetBalances?.loading || false,
      error: assetBalances?.error || null
    };
  }, [assetBalances]);


  const isUserLoggedIn = useSelector((state: any) => state.withcookie?.isUserLoggedIn || false);

  // 로컬 상태 관리
  const [page, setPage] = useState(1);
  const [assetData, setAssetData] = useState<AssetHistoryItem[]>([]);
  const [totalSum, setTotalSum] = useState(0);
  const [totalGotMileage, setTotalGotMileage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // 자산 유형 관련 상태 - 빈 문자열을 명시적으로 설정 (전체 선택)
  const [assetTypes, setAssetTypes] = useState<any[]>([]);
  const [selectedAssetType, setSelectedAssetType] = useState('');

  // 자산 유형별 잔액 정보 로직 추가
  const [assetTypeBalances, setAssetTypeBalances] = useState<{[key: string]: number}>({});

  // 출금 요청 폼 상태
  const [withdrawState, setWithdrawState] = useState<WithdrawFormState>({
    showWithdrawForm: false,
    withdrawAmount: '',
    withdrawToAddress: '',
    withdrawMemo: '',
    withdrawProcessing: false,
    selectedCompanyId: '',
    withdrawDisabledMessage: '',
    feeAmount: 0,
    receiveAmount: 0
  });

  // 환전 모달 상태
  const [exchangeState, setExchangeState] = useState<ExchangeModalState>({
    showExchangeModal: false,
    exchangeFromAsset: null,
    exchangeToAsset: null,
    exchangeAmount: '',
    exchangeReason: '',
    exchangeProcessing: false,
    exchangeResult: null,
    exchangeFeeAmount: 0,
    exchangeReceiveAmount: 0,
    exchangeDisabledMessage: ''
  });

  // 입금 모달 상태
  const [depositState, setDepositState] = useState<DepositModalState>({
    showDepositModal: false,
    depositAsset: null
  });

  // P2P 모달 상태 추가
  const [p2pState, setP2PState] = useState<P2PModalState>({
    showP2PModal: false,
    p2pTargetAsset: null,
    p2pToUserId: '',
    p2pAmount: '',
    p2pReason: '',
    p2pProcessing: false
  });

  // 미리보기 모드에서 사용할 상태
  const [previewSelectedAssetType, setPreviewSelectedAssetType] = useState('all');
  const [previewShowWithdrawForm, setPreviewShowWithdrawForm] = useState(false);
  const [previewShowExchangeModal, setPreviewShowExchangeModal] = useState(false);

  // 자산 유형 변경 핸들러 (먼저 선언)
  const handleAssetTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAssetType(e.target.value);
    // 자산 유형이 변경되어도 전체 내역 표시를 위해 추가 필터링 작업 제거
  }, []);

  // 출금 폼 토글 함수 (먼저 선언)
  const toggleWithdrawForm = useCallback(() => {
    setWithdrawState(prev => ({
      ...prev,
      showWithdrawForm: !prev.showWithdrawForm,
      // 폼을 열 때는 초기화
      withdrawAmount: prev.showWithdrawForm ? prev.withdrawAmount : '',
      withdrawToAddress: prev.showWithdrawForm ? prev.withdrawToAddress : '',
      withdrawMemo: prev.showWithdrawForm ? prev.withdrawMemo : '',
      feeAmount: prev.showWithdrawForm ? prev.feeAmount : 0,
      receiveAmount: prev.showWithdrawForm ? prev.receiveAmount : 0
    }));
  }, []);

  // 입금 모달 관련 함수들 (먼저 선언)
  const openDepositModal = useCallback((assetType: any) => {
    setDepositState(prev => ({
      ...prev,
      showDepositModal: true,
      depositAsset: assetType
    }));
  }, []);

  const closeDepositModal = useCallback(() => {
    setDepositState(prev => ({
      ...prev,
      showDepositModal: false,
      depositAsset: null
    }));
  }, []);

  // 지갑 주소 복사 함수 (먼저 선언)
  const copyWalletAddress = useCallback((address: string) => {
    navigator.clipboard.writeText(address)
      .then(() => {
        alert(t("주소가 클립보드에 복사되었습니다."));
      })
      .catch(err => {
        console.error('주소 복사 실패:', err);
        alert(t("주소 복사에 실패했습니다."));
      });
  }, [t]);

  // 환전 모달 관련 함수들 (먼저 선언)
  const closeExchangeModal = useCallback(() => {
    setExchangeState(prev => ({
      ...prev,
      showExchangeModal: false
    }));
  }, []);

  // API 호출 중복 방지 플래그
  const isLoadingDataRef = useRef(false);
  
  // 데이터 리로드 함수 (강제 새로고침용) 잔액 로드 성공 시 처리
  const loadAssetData = useCallback((currentPage = page) => {
    // 이미 로딩 중이면 중복 호출 방지
    if (isLoadingDataRef.current) {
      console.log('AssetHistory - 이미 로딩 중, 중복 호출 방지');
      return;
    }
    
    isLoadingDataRef.current = true;
    console.log('AssetHistory - API 호출 시작:', { currentPage });
    
    // 두 API를 병렬로 호출하되, 각각 한 번씩만 호출되도록 보장
    Promise.all([
      dispatch(fetchAssetTransactions(currentPage)),
      dispatch(fetchUserAssetBalances())
    ])
      .then(([transactionsResult, balancesResult]) => {
        console.log('AssetHistory - 모든 API 호출 완료');
        // 자산 거래 내역과 잔액 로드 성공 시 처리
      })
      .catch((error: any) => {
        console.error('AssetHistory - API 오류:', error);
      })
      .finally(() => {
        isLoadingDataRef.current = false;
        console.log('AssetHistory - API 호출 상태 초기화');
      });
  }, [dispatch]);

  // 환전 요청 핸들러 추가
  const handleExchangeRequest = useCallback((e: React.FormEvent) => {
    e.preventDefault();

    if (!exchangeState.exchangeAmount || !exchangeState.exchangeFromAsset || !exchangeState.exchangeToAsset) {
      alert(t('환전 금액과 대상 자산을 입력해주세요.'));
      return;
    }

    const amount = Number(exchangeState.exchangeAmount);
    if (isNaN(amount) || amount <= 0) {
      alert(t('올바른 환전 금액을 입력해주세요.'));
      return;
    }


    // 환전 진행 상태로 설정
    setExchangeState(prev => ({
      ...prev,
      exchangeProcessing: true
    }));

    // 출발 자산과 도착 자산의 ID 가져오기
    const fromAssetTypeId = exchangeState.exchangeFromAsset.assetTypeId;
    const toAssetTypeId = exchangeState.exchangeToAsset.assetTypeId;



    // 서버에 요청할 때 클라이언트에서 올바르게 계산한 값을 저장
    const clientCalculatedAmount = exchangeState.exchangeReceiveAmount;

    // 환전 API 호출
    dispatch(exchangeAsset({
      fromAssetTypeId,
      toAssetTypeId,
      amount,
      reason: exchangeState.exchangeReason || '자산 환전 요청'
    }))
      .then((result: any) => {

        if (result.meta && result.meta.requestStatus === 'rejected') {
          // 환전 실패
          const errorMessage = result.payload?.message || t('환전에 실패했습니다.');
          alert(errorMessage);
        } else {
          // API 응답에서 환율 정보와 수수료 정보 확인
          const serverResponse = result.payload;

          // 클라이언트측 계산과 서버측 계산이 다를 경우 확인 메시지 표시
          if (serverResponse?.toAsset?.amount) {
            const serverAmount = Number(serverResponse.toAsset.amount);
            const expectedAmount = Number(clientCalculatedAmount);


            // 서버가 반환한 환율 정보 확인
            const serverExchangeRate = serverResponse.exchange?.exchangeRate || 1450;

            // 환전 완료 메시지에 실제 환전 금액 포함
            const correctAmount = (amount - (serverResponse.fromAsset?.fee || 0)) / serverExchangeRate;
            const assetName = exchangeState.exchangeToAsset?.assetName || 'USDT';
            alert(t(`환전이 성공적으로 처리되었습니다. 환전된 금액: ${correctAmount.toFixed(5)} ${assetName}`));
          } else {
            // 기본 성공 메시지
            alert(t('환전이 성공적으로 처리되었습니다.'));
          }

          // 모달 닫기
          closeExchangeModal();

          // 자산 데이터 새로고침
          loadAssetData();
        }
      })
      .catch((error: any) => {
        console.error('환전 API 오류:', error);
        alert(t('환전 중 오류가 발생했습니다.'));
      })
      .finally(() => {
        // 환전 처리 상태 초기화
        setExchangeState(prev => ({
          ...prev,
          exchangeProcessing: false
        }));
      });
  }, [
    exchangeState,
    t,
    dispatch,
    closeExchangeModal,
    loadAssetData
  ]);

  // 환전 관련 함수 (먼저 선언)
  const handleExchange = useCallback((assetType: any) => {

    // 선택된 자산 정보 찾기 (실제로는 assetType 매개변수가 이미 balance 객체임)
    const assetInfo = assetType; // 전달받은 balance 객체를 그대로 사용

    if (assetInfo) {
      // 환전 설정 확인
      const exchangeInfo = assetInfo.exchange;

      setExchangeState(prev => ({
        ...prev,
        showExchangeModal: true,
        exchangeFromAsset: assetInfo,
        exchangeAmount: '',
        exchangeToAsset: null,
        exchangeReason: '',
        exchangeFeeAmount: 0,
        exchangeReceiveAmount: 0
      }));
    } else {
      alert(t("자산 정보를 찾을 수 없습니다."));
    }
  }, [t]);

  // 투자 관련 함수 수정
  const handleInvestment = useCallback((assetType: any) => {

    // 모달을 열지 않고 /getmembership 페이지로 직접 이동
    window.location.href = '/getmembership';
  }, []);

  // 미리보기 더미 데이터: API 호출 실패 시에만 사용
  const previewData = useMemo(() => {
    return [
      {
        id: 1,
        assetType: { id: 1, name: '포인트' },
        amount: 5000,
        description: '회원가입 포인트',
        desc: '회원가입 포인트',
        createdAt: '2023-01-15',
        transactionType: 'created-system',
        type: 'CREATED_BY_SYSTEM'
      },
      {
        id: 2,
        assetType: { id: 1, name: '포인트' },
        amount: -1000,
        description: '상품 구매',
        desc: '상품 구매',
        createdAt: '2023-01-20',
        transactionType: 'order-normal',
        type: 'ORDER_NORMAL'
      },
      {
        id: 3,
        assetType: { id: 2, name: '마일리지' },
        amount: 1200,
        description: '이벤트 참여 보상',
        desc: '이벤트 참여 보상',
        createdAt: '2023-01-25',
        transactionType: 'created-system',
        type: 'CREATED_BY_SYSTEM'
      }
    ] as AssetHistoryItem[];
  }, []);

  // 미리보기용 자산 유형 데이터
  const previewAssetTypes = useMemo(() => [
    { id: 1, name: '포인트', displayName: '포인트' },
    { id: 2, name: '마일리지', displayName: '마일리지' }
  ], []);

  // 미리보기용 자산 잔액 데이터
  const previewAssetBalances = useMemo(() => [
    {
      assetTypeId: 1,
      assetName: '포인트',
      assetCode: 'POINT',
      iconUrl: 'https://example.com/icons/point.png',
      amount: 5000,
      availableAmount: 5000,
      companyDisplayNames: {
        "1": {
          displayName: '위드쿠키 포인트',
          companyName: '위드쿠키'
        }
      },
      exchange: {
        enabled: true,
        value: 1,
        withdrawMinRest: 500,
        feeType: 'PERCENT',
        feePercent: 5,
        companySettings: [] // 타입 오류 해결을 위해 필드 추가
      },
      lastUpdatedAt: new Date().toISOString()
    },
    {
      assetTypeId: 2,
      assetName: '마일리지',
      assetCode: 'MILEAGE',
      iconUrl: 'https://example.com/icons/mileage.png',
      amount: 2500,
      availableAmount: 2500,
      companyDisplayNames: {
        "1": {
          displayName: '위드쿠키 마일리지',
          companyName: '위드쿠키'
        }
      },
      exchange: {
        enabled: true,
        value: 2,
        withdrawMinRest: 300,
        feeType: 'FIXED',
        feeAmount: 100,
        companySettings: [] // 타입 오류 해결을 위해 필드 추가
      },
      lastUpdatedAt: new Date(Date.now() - 86400000).toISOString()
    }
  ] as AssetBalance[], []);

  // 고유한 자산 유형 추출 함수 - useMemo 사용해 계산 결과 메모이제이션
  const extractUniqueAssetTypes = useCallback((transactions: AssetHistoryItem[]) => {
    // 고유한 자산 유형 맵 생성
    const assetTypeMap = new Map();

    // 트랜잭션에서 고유한 자산 유형 추출
    transactions.forEach(tx => {
      if (tx.assetType?.id && !assetTypeMap.has(tx.assetType.id)) {
        assetTypeMap.set(tx.assetType.id, {
          id: tx.assetType.id,
          name: tx.assetType.name || t('자산'),
          code: tx.assetType.code || '',
          displayName: tx.companyAssetType?.displayName || tx.assetType.name || t('자산'),
        });
      }
    });

    // Map을 배열로 변환
    return Array.from(assetTypeMap.values());
  }, [t]);

  // 트랜잭션 유형을 UI에 맞게 변환하는 함수
  const mapTransactionType = useCallback((type: string) => {
    // 트랜잭션 타입 매핑
    const typeMap: {[key: string]: string} = {
      'CREATED_BY_COMPANY': 'created-system',
      'DESTROYED_BY_COMPANY': 'order-normal',
      'DEPOSIT': 'deposit',
      'WITHDRAWAL': 'withdrawal',
      'ORDER': 'order-normal',
      'WITHDRAWAL_REQUESTED': 'withdrawal',
      'MILEAGE_USED': 'order-normal',
      'CREATED_BY_SYSTEM': 'created-system',
      'ORDER_NORMAL': 'order-normal'
    };

    return typeMap[type] || 'unknown';
  }, []);

  // API 응답을 UI용 데이터로 변환
  const transformTransactionData = useCallback((apiData: any[]): AssetHistoryItem[] => {
    if (!apiData || !Array.isArray(apiData)) return [];


    return apiData.map(item => ({
      id: item.id,
      assetType: item.assetType,
      companyAssetType: item.companyAssetType,
      amount: Number(item.amount),
      description: item.desc || '',
      desc: item.desc || '',
      createdAt: item.createdAt,
      transactionType: mapTransactionType(item.type),
      type: item.type,
      status: item.status || (item.isValid ? 'completed' : 'pending'),
      orderId: item.orderId
    }));
  }, [mapTransactionType]);

  // 자산 유형별 잔액 정보 처리
  useEffect(() => {
    if (balances && balances.length > 0) {
      const balanceMap: {[key: string]: number} = {};
      balances.forEach((balance: AssetBalance) => {
        if (balance.assetTypeId) {
          balanceMap[balance.assetTypeId] = Number(balance.amount || 0);
        }
      });
      setAssetTypeBalances(balanceMap);
    }
  }, [balances]);

  // 선택된 자산 유형의 잔액 정보 가져오기
  const getSelectedAssetTypeBalance = useCallback(() => {
    if (!selectedAssetType) return totalBalance || 0;

    const assetTypeId = parseInt(selectedAssetType);
    return assetTypeBalances[assetTypeId] || 0;
  }, [selectedAssetType, assetTypeBalances, totalBalance]);

  // 선택된 자산 유형의 잔액
  const selectedAssetBalance = useMemo(() => getSelectedAssetTypeBalance(), [getSelectedAssetTypeBalance]);

  // 선택된 자산 정보 가져오기
  const getSelectedAssetTypeInfo = useCallback(() => {
    // 선택한 자산 유형의 정보 찾기
    const selectedAsset = balances.find(
      (balance: AssetBalance) => balance.assetTypeId.toString() === selectedAssetType.toString()
    );

    if (!selectedAsset) {
      return { displayName: t('원'), amount: 0, availableAmount: 0 };
    }

    // 회사별 표시 이름 가져오기 (첫 번째 회사 정보 사용)
    const companyIds = Object.keys(selectedAsset.companyDisplayNames || {});
    const displayInfo = companyIds.length > 0
      ? selectedAsset.companyDisplayNames[companyIds[0]]
      : { displayName: selectedAsset.assetName };

    // 원본 자산 객체 그대로 반환 (withdrawal 속성 포함)
    return {
      ...selectedAsset,
      displayName: displayInfo.displayName || selectedAsset.assetName
    };
  }, [balances, selectedAssetType, t]);

  // 현재 선택된 자산 정보
  const selectedAssetInfo = useMemo(() => {
    return getSelectedAssetTypeInfo();
  }, [getSelectedAssetTypeInfo]);

  // 각 자산 유형별 출금 가능 여부 확인
  const getAssetWithdrawalStatus = useCallback((assetTypeId: number) => {
    const assetInfo = balances.find(
      (balance: AssetBalance) => balance.assetTypeId.toString() === assetTypeId.toString()
    );
    if (!assetInfo || !assetInfo.withdrawal) {
      return { enabled: false, message: t('출금 설정이 없습니다.') };
    }
    if (!assetInfo.withdrawal.enabled) {
      return { enabled: false, message: t('출금이 비활성화되어 있습니다.') };
    }

    // 회사별 출금 설정 확인
    if (assetInfo.withdrawal.companySettings && assetInfo.withdrawal.companySettings.length > 0) {
      const hasEnabledCompany = assetInfo.withdrawal.companySettings.some(
        (company: { companyId: number; companyName: string; enabled: boolean; withdrawalEnabled: boolean }) =>
          company.withdrawalEnabled
      );
      if (!hasEnabledCompany) {
        return { enabled: false, message: t('출금 가능한 회사가 없습니다.') };
      }
      return { enabled: true, message: '' };
    }
    return { enabled: false, message: t('출금 설정이 없습니다.') };
  }, [balances, t]);

  // 환전 가능한 대상 자산 목록 가져오기
  const getExchangeableTargetAssets = useCallback((sourceAssetId: number) => {
    // 환전 가능한 모든 자산(sourceAssetId와 다른 자산)을 반환
    return balances.filter((asset: AssetBalance) => {
      if (asset.assetTypeId === sourceAssetId || !asset.exchange || !asset.exchange.enabled) {
        return false;
      }

      // 회사별 환전 설정 확인
      if (asset.exchange.companySettings && asset.exchange.companySettings.length > 0) {
        // 선택된 회사가 있으면 해당 회사만 확인, 없으면 첫 번째 회사 사용
        const companyIds = Object.keys(asset.companyDisplayNames || {});
        const companyId = withdrawState.selectedCompanyId || (companyIds.length > 0 ? companyIds[0] : null);

        if (companyId) {
          const companySettings = asset.exchange.companySettings.find(
            (company: { companyId: number; exchangeEnabled?: boolean; exchangeToEnabled?: boolean }) =>
              company.companyId.toString() === companyId.toString()
          );

          if (companySettings) {
            return companySettings.exchangeEnabled && companySettings.exchangeToEnabled;
          }
        }

        // 선택된 회사가 없으면 하나라도 환전 가능한 회사가 있는지 확인
        return asset.exchange.companySettings.some(
          (company: { exchangeEnabled?: boolean; exchangeToEnabled?: boolean }) =>
            company.exchangeEnabled && company.exchangeToEnabled
        );
      }

      return true;
    });
  }, [balances, withdrawState.selectedCompanyId]);

  // API 데이터 로드 - 프리뷰와 에디터 모드 모두에서 API 호출하도록 설정
  useEffect(() => {
    // 로그인 상태가 아니면 에러 메시지 표시
    if (!isUserLoggedIn) {
      setApiError(t('로그인이 필요한 서비스입니다.'));
      return;
    }

    loadAssetData();
  }, [isUserLoggedIn, page, dispatch, t, loadAssetData]);

  // API 응답 데이터 감지하여 초기 자산 유형 설정
  useEffect(() => {
    // API에서 잔액 정보가 로드되면 자산 유형 목록 설정
    if (!balancesLoading && balances && balances.length > 0) {

      // 자산 유형 배열 생성
      const assetTypesList = balances.map((balance: AssetBalance) => ({
        id: balance.assetTypeId,
        name: balance.assetName,
        code: balance.assetCode,
        iconUrl: balance.iconUrl,
        displayName: balance.companyDisplayNames && Object.keys(balance.companyDisplayNames).length > 0 ?
          (Object.values(balance.companyDisplayNames)[0] as { displayName: string })?.displayName :
          balance.assetName
      }));

      setAssetTypes(assetTypesList);

      // 자동으로 첫 번째 자산을 선택하지 않음 - 모든 자산 내역을 표시하기 위함
    }
  }, [balances, balancesLoading]);
  
  // 페이지 변경 API 호출 중복 방지 플래그
  const isPageChangingRef = useRef(false);
  
  // 페이지 변경 핸들러
  const handlePageChange = useCallback((newPage: number) => {
    // 이미 페이지 변경 중이면 중복 호출 방지
    if (isPageChangingRef.current) {
      return;
    }
    
    setPage(newPage);

    // 페이지 상단으로 스크롤 (사용자 경험 개선)
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    // 페이지 변경 시 API를 명시적으로 호출 (즉시 데이터 갱신)
    isPageChangingRef.current = true;
    
    dispatch(fetchAssetTransactions(newPage))
      .then((result: any) => {
        // 필터링 없이 데이터를 받아옴
      })
      .catch((error: any) => {
        console.error('페이지 변경 후 거래 내역 로드 실패:', error);
      })
      .finally(() => {
        isPageChangingRef.current = false;
      });
  }, [dispatch]);

  // 로딩 상태 업데이트 - 단일 책임 원칙 적용
  useEffect(() => {
    setIsLoading(transactionsLoading || balancesLoading);
  }, [transactionsLoading, balancesLoading]);

  // 에러 상태 업데이트 - 단일 책임 원칙 적용
  useEffect(() => {
    if (transactionsError) {
      setApiError(transactionsError);
    } else if (balancesError) {
      setApiError(balancesError);
    } else {
      setApiError(null);
    }
  }, [transactionsError, balancesError]);

  // 자산 유형별 액션 핸들러들
  const handleDeposit = useCallback((assetType: any) => {
    // API 호출 또는 모달 표시 로직
    openDepositModal(assetType);
  }, [openDepositModal]);

  const handleWithdrawal = useCallback((assetType: any) => {
    // API 호출 또는 모달 표시 로직

    // 자산 유형 ID 명시적으로 설정 후 폼 표시
    if (assetType.assetTypeId) {
      setSelectedAssetType(assetType.assetTypeId.toString());

      // 수수료 계산을 위한 정보 가져오기
      const withdrawalInfo = assetType.withdrawal;

      // 이후에 출금 폼 토글 - useEffect를 통한 비동기 처리보다 동기적으로 처리
      setTimeout(() => {
        // 회사 ID 설정
        const companyIds = Object.keys(assetType.companyDisplayNames || {});
        const companyId = companyIds.length > 0 ? companyIds[0] : '';

        setWithdrawState(prev => ({
          ...prev,
          showWithdrawForm: true,
          // 출금 금액과 수수료 초기화
          withdrawAmount: '',
          feeAmount: 0,
          receiveAmount: 0,
          selectedCompanyId: companyId,
          withdrawDisabledMessage: ''
        }));
      }, 0);
    } else {
      // 자산 유형 ID가 없는 경우 기본 토글 동작
      toggleWithdrawForm();
    }
  }, [toggleWithdrawForm]);

  // 수수료 계산 함수
  const calculateFee = useCallback((amount: string, assetInfo: any) => {
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0 || !assetInfo) {
      return { feeAmount: 0, receiveAmount: 0 };
    }

    const numAmount = Number(amount);

    // 수수료 정보 확인
    if (!assetInfo.withdrawal || !assetInfo.withdrawal.companySettings || assetInfo.withdrawal.companySettings.length === 0) {
      return { feeAmount: 0, receiveAmount: numAmount };
    }

    // 회사 ID 가져오기
    const companyId = withdrawState.selectedCompanyId;

    // 회사별 수수료 설정 찾기
    const companySetting = companyId
      ? assetInfo.withdrawal.companySettings.find((cs: any) => cs.companyId.toString() === companyId)
      : assetInfo.withdrawal.companySettings[0];

    if (!companySetting || !companySetting.withdrawalSettings) {
      return { feeAmount: 0, receiveAmount: numAmount };
    }

    // 수수료 계산
    let feeAmount = 0;
    const feeSettings = companySetting.withdrawalSettings;

    if (feeSettings.feeType === 'FIXED') {
      feeAmount = Number(feeSettings.fee || 0);
    } else if (feeSettings.feeType === 'PERCENT') {
      feeAmount = numAmount * (Number(feeSettings.feePercent || 0) / 100);
    }

    // 실제 받을 금액 계산
    const receiveAmount = Math.max(0, numAmount - feeAmount);

    return { feeAmount, receiveAmount };
  }, [withdrawState.selectedCompanyId]);

  // 출금 금액 변경 시 수수료 계산
  useEffect(() => {
    if (!withdrawState.withdrawAmount) return;

    const assetInfo = getSelectedAssetTypeInfo();
    const { feeAmount, receiveAmount } = calculateFee(withdrawState.withdrawAmount, assetInfo);

    setWithdrawState(prev => ({
      ...prev,
      feeAmount,
      receiveAmount
    }));
  }, [withdrawState.withdrawAmount, calculateFee, getSelectedAssetTypeInfo]);

  // 출금 금액 입력 처리
  const handleWithdrawAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, ''); // 숫자만 입력 가능
    setWithdrawState(prev => ({ ...prev, withdrawAmount: value }));
  }, []);

  // 출금 요청 핸들러
  const handleWithdrawRequest = useCallback((e: React.FormEvent) => {
    e.preventDefault();

    if (!withdrawState.withdrawAmount) {
      alert(t('출금 금액을 입력해주세요.'));
      return;
    }

    // 출금 주소 검증 코드 제거 (선택 사항으로 변경)

    const assetInfo = getSelectedAssetTypeInfo();

    // 출금 가능 여부 확인
    if (!isWithdrawalEnabled(assetInfo)) {
      alert(withdrawState.withdrawDisabledMessage ||
            getWithdrawalDisabledMessage(assetInfo) ||
            t('출금할 수 없는 자산입니다.'));
      return;
    }

    // 출금 진행 상태로 설정
    setWithdrawState(prev => ({
      ...prev,
      withdrawProcessing: true
    }));

    // 출금 API 호출
    dispatch(requestAssetWithdrawal({
      assetTypeId: Number(selectedAssetType),
      companyId: Number(withdrawState.selectedCompanyId),
      amount: Number(withdrawState.withdrawAmount),
      toAddress: withdrawState.withdrawToAddress,
      desc: withdrawState.withdrawMemo || t('자산 출금 요청')
    }))
    .then((result: any) => {
      if (result.meta && result.meta.requestStatus === 'rejected') {
        console.log(result)
        // 출금 요청 실패
        const errorMessage = result.payload || result.message || t('출금 요청에 실패했습니다!');
        alert(errorMessage);
      } else {
        // 출금 요청 성공
        alert(t(`${assetInfo.assetName} ${withdrawState.withdrawAmount} 출금 요청이 접수되었습니다. (수수료: ${withdrawState.feeAmount}, 실제 출금액: ${withdrawState.receiveAmount})`));

        // 폼 닫기
        setWithdrawState(prev => ({
          ...prev,
          showWithdrawForm: false,
          withdrawAmount: '',
          withdrawToAddress: '',
          withdrawMemo: '',
          feeAmount: 0,
          receiveAmount: 0
        }));

        // 자산 데이터 새로고침
        loadAssetData();
      }
    })
    .catch((error: any) => {
      console.error('출금 요청 API 오류:', error);
      alert(t('출금 요청 중 오류가 발생했습니다.'));
    })
    .finally(() => {
      // 출금 처리 상태 초기화
      setWithdrawState(prev => ({
        ...prev,
        withdrawProcessing: false
      }));
    });
  }, [dispatch, selectedAssetType, withdrawState, getSelectedAssetTypeInfo, t, loadAssetData]);

  // 출금 가능 여부 확인 함수 추가
  const isWithdrawalEnabled = useCallback((assetInfo: any) => {
    if (!assetInfo || !assetInfo.withdrawal) {
      return false;
    }

    // 기본 출금 활성화 여부 확인
    if (!assetInfo.withdrawal.enabled) {
      return false;
    }

    // 출금 회사 설정 확인
    if (assetInfo.withdrawal.companySettings && assetInfo.withdrawal.companySettings.length > 0) {
      // 선택된 회사가 있으면 해당 회사만 확인
      if (withdrawState.selectedCompanyId) {
        const companySettings = assetInfo.withdrawal.companySettings.find(
          (cs: any) => cs.companyId.toString() === withdrawState.selectedCompanyId
        );

        if (companySettings) {
          return companySettings.withdrawalEnabled;
        }
      }

      // 선택된 회사가 없으면 하나라도 출금 가능한 회사가 있는지 확인
      return assetInfo.withdrawal.companySettings.some(
        (company: any) => company.withdrawalEnabled
      );
    }

    return false;
  }, [withdrawState.selectedCompanyId]);

  // 출금 불가 메시지 생성 함수
  const getWithdrawalDisabledMessage = useCallback((assetInfo: any) => {
    if (!assetInfo || !assetInfo.withdrawal) {
      return t('출금 설정이 없습니다.');
    }

    if (!assetInfo.withdrawal.enabled) {
      return t('이 자산은 현재 출금이 비활성화되어 있습니다.');
    }

    // 회사별 출금 설정 확인
    if (assetInfo.withdrawal.companySettings && assetInfo.withdrawal.companySettings.length > 0) {
      // 선택된 회사가 있으면 해당 회사만 확인
      if (withdrawState.selectedCompanyId) {
        const companySettings = assetInfo.withdrawal.companySettings.find(
          (cs: any) => cs.companyId.toString() === withdrawState.selectedCompanyId
        );

        if (companySettings && !companySettings.withdrawalEnabled) {
          return t(`${companySettings.companyName || '선택된 회사'}에서 현재 출금이 비활성화되어 있습니다.`);
        }
      }

      // 출금 가능한 회사가 없는 경우
      const hasEnabledCompany = assetInfo.withdrawal.companySettings.some(
        (company: any) => company.withdrawalEnabled
      );

      if (!hasEnabledCompany) {
        return t('출금 가능한 회사가 없습니다.');
      }
    }

    return '';
  }, [t, withdrawState.selectedCompanyId]);

  // 출금 요청 폼 렌더링
  const renderWithdrawForm = useCallback(() => {
    if (!withdrawState.showWithdrawForm) return null;

    // 선택된 자산 정보 가져오기
    const assetInfo = getSelectedAssetTypeInfo();

    const displayName = assetInfo.displayName || t('원');
    const availableAmount = assetInfo.actualAvailableAmount || assetInfo.availableAmount || 0;

    // 출금 가능 여부 확인
    const withdrawalStatus = getAssetWithdrawalStatus(parseInt(selectedAssetType));
    const withdrawEnabled = withdrawalStatus.enabled;
    const disabledMessage = withdrawState.withdrawDisabledMessage || withdrawalStatus.message;

    return (
      <div className="withdraw-form-container">
        <h3>{t("자산 출금 요청")}</h3>
        <form className="withdraw-form" onSubmit={handleWithdrawRequest}>
          {/* 자산 유형 선택기 */}
          <div className="form-group">
            <label htmlFor="withdraw-asset-type-select">{t("자산 유형")}</label>
            <div className="custom-select-wrapper">
              <select
                id="withdraw-asset-type-select"
                value={selectedAssetType}
                onChange={handleAssetTypeChange}
                disabled={withdrawState.withdrawProcessing || assetTypes.length <= 1} // 하나만 있으면 선택 비활성화
                className="custom-select"
              >
                {assetTypes.length > 0 ? (
                  assetTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name} {type.displayName && type.displayName !== type.name ? `(${type.displayName})` : ''}
                    </option>
                  ))
                ) : (
                  // API 데이터가 없는 경우를 대비한 기본 옵션
                  balances.map((balance: AssetBalance) => (
                    <option key={balance.assetTypeId} value={balance.assetTypeId}>
                      {balance.assetName}
                    </option>
                  ))
                )}
              </select>
              <div className="select-arrow">
                <ArrowDownIcon />
              </div>
            </div>
            {/* 컴팩트한 형태로 배치하고 Fragment 사용 */}
            <>{/* 자산 정보 */}
            <div className="point-available">{t("사용 가능한 자산")}: {availableAmount.toLocaleString()} {displayName}</div>
            {assetInfo.pendingWithdrawals && assetInfo.pendingWithdrawals > 0 ?
              <div className="point-pending">{t("출금 대기 중")}: {assetInfo.pendingWithdrawals.toLocaleString()} {displayName}</div>
            : null}
            <div className="point-available">{t("전체 잔액")}: {assetInfo.amount ? assetInfo.amount.toLocaleString() : '0'} {displayName}</div></>
          </div>

          <div className="form-group">
            <label>{t("출금 금액")}</label>
            <input
              type="text"
              value={withdrawState.withdrawAmount}
              onChange={handleWithdrawAmountChange}
              placeholder={t("출금할 금액을 입력하세요")}
              disabled={withdrawState.withdrawProcessing || !withdrawEnabled}
              required
              className="input-styled"
            />

            {/* 수수료 및 실제 받을 금액 표시 */}
            {withdrawState.withdrawAmount && Number(withdrawState.withdrawAmount) > 0 && (
              <div className="fee-calculation">
                <div className="fee-info">
                  {t("수수료")}: {withdrawState.feeAmount.toLocaleString()} {displayName}
                </div>
                <div className="receive-info">
                  {t("실제 받을 금액")}: {withdrawState.receiveAmount.toLocaleString()} {displayName}
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>{t("출금 주소")} ({t("선택사항")})</label>
            <input
              type="text"
              value={withdrawState.withdrawToAddress}
              onChange={(e) => setWithdrawState(prev => ({ ...prev, withdrawToAddress: e.target.value }))}
              placeholder={t("출금할 주소를 입력하세요 (선택사항)")}
              disabled={withdrawState.withdrawProcessing || !withdrawEnabled}
              className="input-styled"
            />
            <div className="address-helper">
              {t("선택한 자산의 올바른 주소 형식을 입력해주세요.")}
            </div>
          </div>

          <div className="form-group">
            <label>{t("메모 (선택사항)")}</label>
            <input
              type="text"
              value={withdrawState.withdrawMemo}
              onChange={(e) => setWithdrawState(prev => ({ ...prev, withdrawMemo: e.target.value }))}
              placeholder={t("메모를 입력하세요")}
              disabled={withdrawState.withdrawProcessing || !withdrawEnabled}
              className="input-styled"
            />
          </div>

          {!withdrawEnabled && disabledMessage && (
            <div className="withdrawal-disabled-message">
              {disabledMessage}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={toggleWithdrawForm}
              disabled={withdrawState.withdrawProcessing}
            >
              {t("취소")}
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={
                withdrawState.withdrawProcessing ||
                !withdrawEnabled ||
                !withdrawState.withdrawAmount
              }
            >
              {withdrawState.withdrawProcessing ? t('처리 중...') : t('출금 요청')}
            </button>
          </div>
        </form>
      </div>
    );
  }, [
    withdrawState,
    selectedAssetType,
    assetTypes,
    balances,
    handleAssetTypeChange,
    t,
    getSelectedAssetTypeInfo,
    getAssetWithdrawalStatus,
    toggleWithdrawForm,
    handleWithdrawAmountChange,
    handleWithdrawRequest
  ]);

  // 입금 모달 렌더링
  const renderDepositModal = useCallback(() => {
    if (!depositState.showDepositModal || !depositState.depositAsset || !depositState.depositAsset.wallet) return null;

    const walletAddress = depositState.depositAsset.wallet.address;
    const networkName = depositState.depositAsset.wallet.blockchainNetworkId === 1 ? 'TRON' : t('알 수 없음');

    // 회사별 표시 이름 가져오기
    const companyIds = Object.keys(depositState.depositAsset.companyDisplayNames || {});
    const displayInfo = companyIds.length > 0 && depositState.depositAsset.companyDisplayNames
      ? depositState.depositAsset.companyDisplayNames[companyIds[0]]
      : { displayName: depositState.depositAsset.assetName };

    return (
      <div
        className="modal-overlay"
        onClick={(e) => {
          // 모달 바깥 영역 클릭 시 모달 닫기
          if (e.target === e.currentTarget) {
            closeDepositModal();
          }
        }}
      >
        <div className="deposit-modal">
          <div className="modal-header">
            <h3>{t("자산 입금")}</h3>
            <button
              className="close-button"
              onClick={closeDepositModal}
            >&times;</button>
          </div>
          <div className="modal-content">
            <div className="warning-message">
              <span>⚠️</span>
              <span>
                {t("주의: 반드시 올바른 네트워크를 확인하고 입금하세요. 잘못된 네트워크로 입금 시 자산이 손실될 수 있습니다.")}
              </span>
            </div>

            <div className="asset-info">
              <div className="asset-icon">
                <AssetIcon
                  iconUrl={depositState.depositAsset.iconUrl}
                  assetName={depositState.depositAsset.assetName}
                />
              </div>
              <div>
                <div className="asset-name">{depositState.depositAsset.assetName}</div>
                <div className="asset-display-name">{displayInfo.displayName}</div>
              </div>
            </div>

            <div className="wallet-info">
              <div className="network-info">
                <label>{t("네트워크")}:</label>
                <div>{networkName}</div>
              </div>

              <div className="address-info">
                <label>{t("입금 주소")}:</label>
                <div>
                  {walletAddress}
                  <button
                    onClick={() => copyWalletAddress(walletAddress)}
                    title={t("주소 복사")}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <div className="qr-code">
              <div>{t("QR 코드 스캔")}</div>

              <div>
                <QRCodeSVG
                  value={walletAddress}
                  size={200}
                  bgColor={"#ffffff"}
                  fgColor={"#000000"}
                  level={"H"}
                  includeMargin={true}
                  title={t("지갑 주소 QR 코드")}
                />
              </div>
            </div>

            <div className="note">
              {t("입금 완료 후 블록체인 네트워크 상황에 따라 반영까지 시간이 소요될 수 있습니다.")}
            </div>
          </div>
        </div>
      </div>
    );
  }, [depositState, t, closeDepositModal, copyWalletAddress]);

  // 환전 금액 계산 함수 추가
  const calculateExchangeAmount = useCallback((amount: string, fromAsset: any, toAsset: any) => {
    if (!amount || !fromAsset || !toAsset || !fromAsset.exchange || !toAsset.exchange) {
      return { feeAmount: 0, receiveAmount: 0 };
    }

    const numAmount = Number(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      return { feeAmount: 0, receiveAmount: 0 };
    }

    // 회사 ID 가져오기
    const companyIds = Object.keys(fromAsset.companyDisplayNames || {});
    const companyId = companyIds.length > 0 ? companyIds[0] : null;

    if (!companyId) {
      return { feeAmount: 0, receiveAmount: 0 };
    }

    // 출발 자산의 환전 설정 찾기
    const fromAssetCompanySettings = fromAsset.exchange.companySettings.find(
      (cs: any) => cs.companyId.toString() === companyId.toString()
    );

    // 도착 자산의 환전 설정 찾기
    const toAssetCompanySettings = toAsset.exchange.companySettings?.find(
      (cs: any) => cs.companyId.toString() === companyId.toString()
    );

    if (!fromAssetCompanySettings || !fromAssetCompanySettings.exchangeSettings) {
      return { feeAmount: 0, receiveAmount: 0 };
    }

    const exchangeSettings = fromAssetCompanySettings.exchangeSettings;

    // 수수료 계산 - 출발 자산 단위로 계산
    let feeAmount = 0;
    if (exchangeSettings.feeType === 'FIXED') {
      feeAmount = Number(exchangeSettings.fee || 0);
    } else if (exchangeSettings.feeType === 'PERCENT') {
      feeAmount = numAmount * (Number(exchangeSettings.feePercent || 0) / 100);
    }

    // 수수료를 제외한 금액 계산 (출발 자산 단위)
    const amountAfterFee = numAmount - feeAmount;

    // 환율 계산 - 출발 자산과 도착 자산의 가치 비율로 계산
    const fromValue = fromAssetCompanySettings.exchangeSettings?.value || 1;
    const toValue = toAssetCompanySettings?.exchangeSettings?.value || 1;
    const exchangeRate = fromValue / toValue;

    // 환전 결과 금액 계산 - 수수료 차감 후 금액에 환율 곱하기
    const receiveAmount = amountAfterFee * exchangeRate;


    return { feeAmount, receiveAmount };
  }, []);

  // 환전 금액 변경 시 수수료와 환전 결과 계산 추가
  useEffect(() => {
    if (!exchangeState.exchangeAmount || !exchangeState.exchangeFromAsset || !exchangeState.exchangeToAsset) {
      return;
    }

    const { feeAmount, receiveAmount } = calculateExchangeAmount(
      exchangeState.exchangeAmount,
      exchangeState.exchangeFromAsset,
      exchangeState.exchangeToAsset
    );

    setExchangeState(prev => ({
      ...prev,
      exchangeFeeAmount: feeAmount,
      exchangeReceiveAmount: receiveAmount
    }));
  }, [
    exchangeState.exchangeAmount,
    exchangeState.exchangeFromAsset,
    exchangeState.exchangeToAsset,
    calculateExchangeAmount
  ]);

  // 환전 모달 렌더링
  const renderExchangeModal = useCallback(() => {
    // 모달이 닫혀있으면 렌더링하지 않음
    if (!exchangeState.showExchangeModal) return null;

    // 환전 가능한 대상 자산 목록
    const targetAssets = exchangeState.exchangeFromAsset
      ? getExchangeableTargetAssets(exchangeState.exchangeFromAsset.assetTypeId)
      : [];

    // 출발 자산 정보 표시
    const fromAssetName = exchangeState.exchangeFromAsset?.assetName || '';
    const fromAssetCode = exchangeState.exchangeFromAsset?.assetCode || '';
    const fromAssetAvailableAmount = exchangeState.exchangeFromAsset?.actualAvailableAmount ||
                                   exchangeState.exchangeFromAsset?.availableAmount || 0;

    // 회사 ID 가져오기 - 환전 정보 표시용
    const companyIds = exchangeState.exchangeFromAsset &&
                      exchangeState.exchangeFromAsset.companyDisplayNames ?
                      Object.keys(exchangeState.exchangeFromAsset.companyDisplayNames) : [];
    const companyId = companyIds.length > 0 ? companyIds[0] : null;

    // 자산의 displayName 올바르게 추출
    const fromAssetDisplayName = companyId &&
                              exchangeState.exchangeFromAsset?.companyDisplayNames &&
                              exchangeState.exchangeFromAsset.companyDisplayNames[companyId]?.displayName ||
                              exchangeState.exchangeFromAsset?.assetName || '';

    // 도착 자산 정보
    const toCompanyIds = exchangeState.exchangeToAsset &&
                      exchangeState.exchangeToAsset.companyDisplayNames ?
                      Object.keys(exchangeState.exchangeToAsset.companyDisplayNames) : [];
    const toCompanyId = toCompanyIds.length > 0 ? toCompanyIds[0] : null;

    const toAssetDisplayName = toCompanyId &&
                            exchangeState.exchangeToAsset?.companyDisplayNames &&
                            exchangeState.exchangeToAsset.companyDisplayNames[toCompanyId]?.displayName ||
                            exchangeState.exchangeToAsset?.assetName ||
                            t('USDT');
    const toAssetName = exchangeState.exchangeToAsset?.assetName || '';
    const toAssetCode = exchangeState.exchangeToAsset?.assetCode || '';

    // 환전 설정 정보 가져오기
    let fromValue = 1;
    let toValue = 1;
    let exchangeRate = 1;
    let feeType = 'FIXED';
    let feePercentOrFixed = 0;

    if (companyId && exchangeState.exchangeFromAsset && exchangeState.exchangeFromAsset.exchange) {
      const fromCompanySettings = exchangeState.exchangeFromAsset.exchange.companySettings.find(
        (cs: any) => cs.companyId.toString() === companyId.toString()
      );

      const toCompanySettings = exchangeState.exchangeToAsset?.exchange?.companySettings?.find(
        (cs: any) => cs.companyId.toString() === companyId.toString()
      );

      if (fromCompanySettings?.exchangeSettings) {
        fromValue = Number(fromCompanySettings.exchangeSettings.value || 1);
        feeType = fromCompanySettings.exchangeSettings.feeType || 'FIXED';
        feePercentOrFixed = feeType === 'FIXED' ?
                           Number(fromCompanySettings.exchangeSettings.fee || 0) :
                           Number(fromCompanySettings.exchangeSettings.feePercent || 0);
      }

      if (toCompanySettings?.exchangeSettings) {
        toValue = Number(toCompanySettings.exchangeSettings.value || 1);
      }

      // 환율 계산
      exchangeRate = fromValue / toValue;
    }

    return (
      <div
        className="modal-overlay"
        onClick={(e) => {
          // 모달 바깥 영역 클릭 시 모달 닫기 (처리 중일 때는 닫지 않음)
          if (e.target === e.currentTarget && !exchangeState.exchangeProcessing) {
            closeExchangeModal();
          }
        }}
      >
        <div className="exchange-modal">
          <div className="modal-header">
            <h3>{t("자산 환전")}</h3>
            <button
              className="close-button"
              onClick={closeExchangeModal}
              disabled={exchangeState.exchangeProcessing}
            >&times;</button>
          </div>
          <form className="exchange-form" onSubmit={handleExchangeRequest}>


            {/* 환전 출발 자산 정보 */}
            <div className="form-group">
              <label>{t("환전 출발 자산")}</label>
              <div className="asset-info-box">
                <div className="asset-icon">
                  <AssetIcon
                    iconUrl={exchangeState.exchangeFromAsset?.iconUrl}
                    assetName={exchangeState.exchangeFromAsset?.assetName}
                  />
                </div>
                <div className="asset-name">
                  {exchangeState.exchangeFromAsset?.assetName}
                </div>
                <div className="asset-available">
                  {t("사용 가능")}: {fromAssetAvailableAmount.toLocaleString()} {fromAssetDisplayName}
                </div>
              </div>
            </div>

            {/* 환전 도착 자산 정보 */}
            <div className="form-group">
              <label htmlFor="exchange-to-asset">{t("환전 도착 자산")}</label>
              <div className="custom-select-wrapper">
                <select
                  id="exchange-to-asset"
                  value={exchangeState.exchangeToAsset?.assetTypeId || ''}
                  onChange={(e) => {
                    const targetAssetId = e.target.value;
                    if (!targetAssetId) {
                      setExchangeState(prev => ({ ...prev, exchangeToAsset: null }));
                      return;
                    }

                    const targetAsset = balances.find(
                      (asset: AssetBalance) => asset.assetTypeId.toString() === targetAssetId
                    );

                    setExchangeState(prev => ({ ...prev, exchangeToAsset: targetAsset || null }));
                  }}
                  disabled={exchangeState.exchangeProcessing || targetAssets.length <= 0}
                  required
                  className="custom-select"
                >
                  <option value="">{t("환전할 자산 선택")}</option>
                  {targetAssets.map((asset: AssetBalance) => (
                    <option key={asset.assetTypeId} value={asset.assetTypeId}>
                      {asset.assetName} {asset.displayName && asset.displayName !== asset.assetName ? `(${asset.displayName})` : ''}
                    </option>
                  ))}
                </select>
                <div className="select-arrow">
                  <ArrowDownIcon />
                </div>
              </div>
            </div>
            {/* 환전 정보 표시 */}
            <div className="exchange-info" style={{
              margin: '0 0 16px',
              padding: '10px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              fontSize: '14px'
            }}>
              <div><strong>{t("환전 비율")}:</strong> 1 {fromAssetDisplayName || fromAssetName} = {exchangeRate.toFixed(4)} {toAssetDisplayName || toAssetName}</div>
              <div>
                <strong>{t("수수료")}:</strong> {
                  feeType === 'FIXED' ?
                  `${feePercentOrFixed} ${fromAssetDisplayName || fromAssetName}` :
                  `${feePercentOrFixed}% (${fromAssetDisplayName || fromAssetName})`
                }
              </div>
            </div>
            {/* 환전 금액 */}
            <div className="form-group">
              <label>{t("환전 금액")}</label>
              <input
                type="text"
                value={exchangeState.exchangeAmount}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, ''); // 숫자만 입력 가능
                  setExchangeState(prev => ({ ...prev, exchangeAmount: value }));
                }}
                placeholder={t("환전할 금액을 입력하세요")}
                disabled={exchangeState.exchangeProcessing || !exchangeState.exchangeToAsset}
                required
                className="input-styled"
              />

              {/* 수수료 및 실제 환전 금액 표시 */}
              {exchangeState.exchangeAmount && Number(exchangeState.exchangeAmount) > 0 && exchangeState.exchangeToAsset && (
                <div className="fee-calculation">
                  <div className="fee-info">
                    {t("수수료")}: {exchangeState.exchangeFeeAmount.toLocaleString()} {fromAssetDisplayName || fromAssetName}
                  </div>
                  <div className="receive-info">
                    {t("환전 결과")}: {exchangeState.exchangeReceiveAmount.toFixed(5).toLocaleString()} {toAssetDisplayName || toAssetName}
                  </div>
                </div>
              )}
            </div>

            {/* 환전 사유 */}
            <div className="form-group">
              <label>{t("환전 사유 (선택사항)")}</label>
              <input
                type="text"
                value={exchangeState.exchangeReason}
                onChange={(e) => setExchangeState(prev => ({ ...prev, exchangeReason: e.target.value }))}
                placeholder={t("환전 사유를 입력하세요")}
                disabled={exchangeState.exchangeProcessing}
                className="input-styled"
              />
            </div>

            {exchangeState.exchangeDisabledMessage && (
              <div className="exchange-disabled-message">
                {exchangeState.exchangeDisabledMessage}
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={closeExchangeModal}
                disabled={exchangeState.exchangeProcessing}
              >
                {t("취소")}
              </button>
              <button
                type="submit"
                className="submit-btn"
                disabled={
                  exchangeState.exchangeProcessing ||
                  !exchangeState.exchangeToAsset ||
                  !exchangeState.exchangeAmount
                }
              >
                {exchangeState.exchangeProcessing ? t('처리 중...') : t('환전하기')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }, [
    exchangeState,
    t,
    getExchangeableTargetAssets,
    balances,
    closeExchangeModal,
    handleExchangeRequest
  ]);

  // 필터링된 자산 데이터 계산
  const filteredAssetData = useMemo(() => {
    // API에서 가져온 데이터가 있으면 사용
    if (transactionHistory && transactionHistory.length > 0) {
      // 필터링 없이 모든 내역 반환
      return transactionHistory;
    }

    // API에서 데이터가 없거나 로딩 중인 경우 빈 배열 반환
    return [];
  }, [transactionHistory]);

  // P2P 모달 열기 함수
  const openP2PTransferModal = useCallback((asset: any) => {
    setP2PState({
      ...p2pState,
      showP2PModal: true,
      p2pTargetAsset: asset,
      p2pToUserId: '',
      p2pAmount: '',
      p2pReason: ''
    });
  }, [p2pState]);

  // P2P 모달 닫기 함수
  const closeP2PTransferModal = useCallback(() => {
    setP2PState({
      ...p2pState,
      showP2PModal: false
    });
  }, [p2pState]);

  // P2P 입력값 변경 핸들러
  const handleP2PInputChange = useCallback((field: keyof P2PModalState, value: string) => {
    setP2PState({
      ...p2pState,
      [field]: value
    });
  }, [p2pState]);

  // P2P 요청 처리 함수
  const handleP2PTransfer = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isUserLoggedIn) {
      alert(t('로그인이 필요한 서비스입니다.'));
      return;
    }
    if (!p2pState.p2pTargetAsset) return;
    if (!p2pState.p2pToUserId) {
      alert(t('받는 분의 Id를 입력해주세요.'));
      return;
    }
    if (!p2pState.p2pAmount || isNaN(Number(p2pState.p2pAmount)) || Number(p2pState.p2pAmount) <= 0) {
      alert(t('유효한 송금 금액을 입력해주세요.'));
      return;
    }
    if ((p2pState.p2pTargetAsset.availableAmount || 0) < Number(p2pState.p2pAmount)) {
      alert(t('보유 자산보다 많은 금액을 송금할 수 없습니다.'));
      return;
    }
    try {
      setP2PState({
        ...p2pState,
        p2pProcessing: true
      });

      const requestData = {
        assetTypeId: p2pState.p2pTargetAsset.assetTypeId || p2pState.p2pTargetAsset.id,
        toInsungId: p2pState.p2pToUserId,
        amount: Number(p2pState.p2pAmount),
        reason: p2pState.p2pReason || t('P2P')
      };

      // Redux 액션 디스패치
      const resultAction = await dispatch(transferAssetP2P(requestData));

      if (transferAssetP2P.fulfilled.match(resultAction)) {
        // 송금이 성공한 경우, 먼저 모달을 닫고 상태를 초기화합니다
        setP2PState({
          showP2PModal: false,
          p2pTargetAsset: null,
          p2pToUserId: '',
          p2pAmount: '',
          p2pReason: '',
          p2pProcessing: false
        });

        // 자산 정보 업데이트 (백그라운드로 처리)
        dispatch(fetchUserAssetBalances());
        dispatch(fetchAssetTransactions(1));
        setPage(1);

        // 모달이 닫힌 후 알림 표시
        setTimeout(() => {
          alert(t('송금이 완료되었습니다.'));
        }, 100);
      } else if (transferAssetP2P.rejected.match(resultAction)) {
        const errorMsg = resultAction.payload
          ? (resultAction.payload as any).message
          : t('송금 요청 실패');
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      alert(`${t('송금 요청 중 오류가 발생했습니다')}: ${err.message}`);
      // 오류 발생 시 처리 상태만 false로 변경하고 모달은 닫지 않음
      setP2PState(prev => ({
        ...prev,
        p2pProcessing: false
      }));
    }
  }, [dispatch, isUserLoggedIn, p2pState, t]);

  // P2P 모달 렌더링 함수
  const renderP2PModal = useCallback(() => {
    if (!p2pState.showP2PModal || !p2pState.p2pTargetAsset) return null;

    const displayInfo = (() => {
      const companyIds = Object.keys(p2pState.p2pTargetAsset.companyDisplayNames || {});
      return companyIds.length > 0
        ? p2pState.p2pTargetAsset.companyDisplayNames[companyIds[0]]
        : { displayName: p2pState.p2pTargetAsset.assetName };
    })();

    return (
      <div
        className="modal-overlay"
        onClick={(e) => {
          // 모달 바깥 영역 클릭 시 모달 닫기 (처리 중일 때는 닫지 않음)
          if (e.target === e.currentTarget && !p2pState.p2pProcessing) {
            closeP2PTransferModal();
          }
        }}
      >
        <div className="exchange-modal">
          <div className="modal-header">
            <h3>{t('P2P')}</h3>
            <button className="close-button" onClick={closeP2PTransferModal} disabled={p2pState.p2pProcessing}>&times;</button>
          </div>
          <form className="exchange-form" onSubmit={handleP2PTransfer}>
            <div className="form-group">
              <label>{t('보내는 자산')}</label>
              <div>{p2pState.p2pTargetAsset.assetName} ({displayInfo.displayName})</div>
              <div>{t('사용 가능')}: {(p2pState.p2pTargetAsset.availableAmount || 0).toLocaleString()} {displayInfo.displayName}</div>
            </div>
            <div className="form-group">
              <label>{t('받는 분 Id')}</label>
              <input
                type="text"
                value={p2pState.p2pToUserId}
                onChange={(e) => handleP2PInputChange('p2pToUserId', e.target.value)}
                required
                disabled={p2pState.p2pProcessing}
                className="input-styled"
              />
            </div>
            <div className="form-group">
              <label>{t('송금 금액')}</label>
              <input
                type="text"
                value={p2pState.p2pAmount}
                onChange={(e) => handleP2PInputChange('p2pAmount', e.target.value.replace(/[^0-9]/g, ''))}
                required
                disabled={p2pState.p2pProcessing}
                className="input-styled"
              />
            </div>
            <div className="form-group">
              <label>{t('송금 사유 (선택)')}</label>
              <input
                type="text"
                value={p2pState.p2pReason}
                onChange={(e) => handleP2PInputChange('p2pReason', e.target.value)}
                disabled={p2pState.p2pProcessing}
                className="input-styled"
              />
            </div>
            <div className="form-actions">
              <button type="button" className="cancel-btn" onClick={closeP2PTransferModal} disabled={p2pState.p2pProcessing}>{t('취소')}</button>
              <button type="submit" className="submit-btn" disabled={p2pState.p2pProcessing}>{p2pState.p2pProcessing ? t('처리 중...') : t('송금하기')}</button>
            </div>
          </form>
        </div>
      </div>
    );
  }, [p2pState, closeP2PTransferModal, handleP2PTransfer, t, handleP2PInputChange]);

  // 투자 버튼 표시 여부 함수 수정 및 디버깅 로그 추가
  const shouldShowInvestmentButton = useCallback((asset: any) => {
    // 회사 ID와 자산 이름 디버깅
    const companyIdNum = Number(companyId);
    const isAllowedCompany = companyIdNum === 21 || companyIdNum === 24 || companyIdNum === 26;
    // 대소문자 구분 없이 'RP' 또는 'Rp' 자산 확인
    const isRpAsset = asset.assetName.toUpperCase() === 'RP';

    // 상세 디버깅 정보 로깅

    return isAllowedCompany && isRpAsset;
  }, [companyId]);




  return (
    <div className={clsx(componentProps?.className)} id={props.id} style={style}>
      <div className="container">
        <h2 className="mileage-history-title">{title}</h2>



        {/* Asset Details 섹션 추가 */}
        <div className="asset-details-section">


          {balancesLoading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <span>{t("자산 정보 로딩 중...")}</span>
            </div>
          ) : balancesError ? (
            <div className="error-message">
              {t("자산 정보를 불러오는 중 오류가 발생했습니다.")}
            </div>
          ) : (
            <>


              <div className="asset-cards-container">
                {balances && balances.length > 0 ? (
                  balances.map((balance: AssetBalance) => {
                    // 자산 유형과 회사별 표시 이름 가져오기
                    const companyIds = Object.keys(balance.companyDisplayNames || {});
                    const displayInfo = companyIds.length > 0 && balance.companyDisplayNames
                      ? balance.companyDisplayNames[companyIds[0]]
                      : { displayName: balance.assetName };

                    // 출금 가능 여부 확인
                    const withdrawalStatus = getAssetWithdrawalStatus(balance.assetTypeId);

                    // 사용 가능 금액
                    const availableAmount = balance.actualAvailableAmount || balance.availableAmount || 0;

                    return (
                      <div key={balance.assetTypeId} className="asset-card">
                        <div className="asset-card-header">
                          <div className="asset-card-icon">
                            <AssetIcon iconUrl={balance.iconUrl} assetName={balance.assetName} />
                          </div>
                          <div className="asset-card-name">
                            {balance.assetName}
                            {displayInfo.displayName !== balance.assetName && (
                              <span className="display-name"> ({displayInfo.displayName})</span>
                            )}
                          </div>
                        </div>

                        <div className="asset-card-actions">
                          {/* 입금 버튼 - 지갑 정보가 있을 경우에만 표시 */}
                          {balance.wallet && balance.wallet.address && (
                            <button
                              className="action-button deposit"
                              onClick={() => handleDeposit(balance)}
                            >
                              {t("입금")}
                            </button>
                          )}

                          {/* 출금 버튼 - 출금 가능할 경우에만 표시 */}
                          {withdrawalStatus.enabled && (
                            <button
                              className="action-button withdrawal"
                              onClick={() => handleWithdrawal(balance)}
                            >
                              {t("출금")}
                            </button>
                          )}

                          {/* 환전 버튼 - 환전 가능할 경우에만 표시 */}
                          {balance.exchange && balance.exchange.enabled && (
                            (() => {
                              // 회사 ID 가져오기
                              const companyIds = Object.keys(balance.companyDisplayNames || {});
                              const companyId = companyIds.length > 0 ? companyIds[0] : null;

                              // 회사별 환전 설정 찾기
                              const companySettings = companyId && balance.exchange.companySettings
                                ? balance.exchange.companySettings.find(
                                    (cs: any) => cs.companyId.toString() === companyId.toString()
                                  )
                                : null;

                              // 환전 가능 여부 확인
                              const exchangeEnabled = companySettings &&
                                companySettings.exchangeEnabled &&
                                companySettings.exchangeFromEnabled;

                              return exchangeEnabled ? (
                                <button
                                  className="action-button exchange"
                                  onClick={() => handleExchange(balance)}
                                >
                                  {t("환전")}
                                </button>
                              ) : null;
                            })()
                          )}

                          {/* P2P 버튼 - P2P 가능할 경우에만 표시 */}
                          {isP2PEnabled(balance) && (
                            <button
                              className="action-button p2p"
                              onClick={() => openP2PTransferModal(balance)}
                            >
                              {t("P2P")}
                            </button>
                          )}

                          {/* 투자 버튼 - 특정 회사와 Rp 자산에만 표시 */}
                          {(() => {
                            const shouldShow = shouldShowInvestmentButton(balance);

                            return shouldShow ? (
                              <button
                                className="action-button investment"
                                onClick={() => handleInvestment(balance)}
                              >
                                {t("투자")}
                              </button>
                            ) : null;
                          })()}
                        </div>

                        <div className="asset-card-balance">
                          {/* React Fragment를 사용하여 불필요한 렌더링 방지 */}
                          <React.Fragment>
                            <div className="balance-amount-large">
                              {Number(balance.amount || 0).toLocaleString()} {displayInfo.displayName}
                            </div>


                            {Number(availableAmount) !== Number(balance.amount) && (
                              <div className="balance-available">
                                {t("사용 가능")}: {Number(availableAmount).toLocaleString()} {displayInfo.displayName}
                              </div>
                            )}

                            {/* pendingWithdrawals가 undefined가 아니고 0보다 큰 경우에만 렌더링 */}
                            {balance.pendingWithdrawals && balance.pendingWithdrawals > 0 ? (
                              <div className="asset-pending-amount">
                                {t("출금 대기")}: {Number(balance.pendingWithdrawals).toLocaleString()} {displayInfo.displayName}
                              </div>
                            ) : null}
                          </React.Fragment>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="empty-state">
                    {t("자산 정보가 없습니다.")}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* 출금 폼 */}
        {withdrawState.showWithdrawForm && renderWithdrawForm()}

        {/* 환전 모달 */}
        {exchangeState.showExchangeModal && renderExchangeModal()}

        {/* 입금 모달 */}
        {depositState.showDepositModal && renderDepositModal()}

        {/* P2P 모달 */}
        {p2pState.showP2PModal && renderP2PModal()}



        {/* 자산 유형 선택기 제거 - 항상 전체 내역 표시 */}


        {/* 자산 내역 리스트 렌더링 */}
        {apiError && (
          <div className="error-message">
            {apiError}
          </div>
        )}

        {isLoading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <span>{t("로딩 중...")}</span>
          </div>
        ) : filteredAssetData.length > 0 ? (
          <div className="mileage-history-content">
            <div className="mileage-history-list">
              <div className="mileage-history-header">
                <div className="mh-col date">{t("날짜")}</div>
                <div className="mh-col mileage-description">{t("내역")}</div>
                <div className="mh-col amount">{t("금액")}</div>
              </div>

              {filteredAssetData.map((item: AssetHistoryItem) => {
                return (
                  <div className="mileage-item" key={item.id}>
                    <div className="mh-col date">
                      {formatDate(item.createdAt)}
                    </div>
                    <div className="mh-col mileage-description">
                      <span className={`mileage-type type-${item.transactionType || 'unknown'}`}>
                        {item.desc || item.description}
                      </span>

                      {item.status === 'pending' && (
                        <span className="status-badge pending">
                          {t("승인 대기중")}
                        </span>
                      )}

                      {item.orderId && (
                        <span className="order-link">
                          {t("주문번호")}: <a href={`/orders/${item.orderId}`}>{item.orderId}</a>
                        </span>
                      )}
                    </div>
                    <div className={`mh-col amount ${item.amount > 0 ? 'positive' : 'negative'}`}>
                      {formatAmount(item.amount)}
                      {(() => {
                        // 1. 회사 자산 유형에서 displayName 찾기
                        if (item.companyAssetType?.displayName) {
                          return item.companyAssetType.displayName;
                        }

                        // 2. 자산 유형에서 회사별 표시 이름 찾기
                        const assetTypeId = item.assetType?.id;
                        if (assetTypeId) {
                          // balances 배열에서 해당 자산 찾기
                          const matchingAsset = balances.find(
                            (balance: AssetBalance) => balance.assetTypeId === assetTypeId
                          );

                          // 해당 자산의 companyDisplayNames에서 현재 회사에 맞는 displayName 찾기
                          if (matchingAsset && matchingAsset.companyDisplayNames && companyId) {
                            const companyIdStr = companyId.toString();
                            const displayName = matchingAsset.companyDisplayNames[companyIdStr]?.displayName;
                            if (displayName) return displayName;
                          }
                        }

                        // 3. 자산 코드 사용
                        if (item.assetType?.code) {
                          return item.assetType.code;
                        }

                        // 4. 기본값
                        return '';
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="empty-state">
            {emptyText}
          </div>
        )}

        {/* 페이지네이션 영역을 별도의 div로 감싸 레이아웃 관리 */}
        <div className="pagination-container-wrapper">
          {/* pagination.page가 undefined일 경우 로컬 state의 page 사용 */}
          <SimplePagination
            currentPage={pagination.page || page}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default AssetHistoryComponent;

// 단순화된 페이지네이션 컴포넌트
const SimplePagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}> = ({ currentPage, totalPages, onPageChange }) => {
  // totalPages가 0이면 아무것도 렌더링하지 않음
  if (totalPages === 0) return null;
  // 현재 페이지 그룹 계산 (1-5, 6-10, ...)
  const pageGroupSize = 5;
  const currentGroup = Math.ceil(currentPage / pageGroupSize);
  const startPage = (currentGroup - 1) * pageGroupSize + 1;
  const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);
  // 실제로 표시할 페이지 번호들
  const pageNumbers = [];
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }
  return (
    <div className="pagination" style={{ display: 'flex', justifyContent: 'center', gap: '8px', margin: '20px 0' }}>
      {/* 첫 페이지 버튼 */}
      <button
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
        className="page-button"
        style={{ cursor: 'pointer', padding: '8px 12px' }}
      >
        &laquo;
      </button>
      {/* 이전 페이지 버튼 */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="page-button prev"
        style={{ cursor: 'pointer', padding: '8px 12px' }}
      >
        &lt;
      </button>
      {/* 페이지 번호 버튼들 */}
      {pageNumbers.map(num => (
        <button
          key={num}
          onClick={() => onPageChange(num)}
          className={`page-button ${num === currentPage ? 'active' : ''}`}
          style={{
            cursor: 'pointer',
            padding: '8px 12px',
            backgroundColor: num === currentPage ? '#3b82f6' : 'white',
            color: num === currentPage ? 'white' : 'black',
            fontWeight: num === currentPage ? 'bold' : 'normal',
            border: '1px solid #e5e7eb',
            borderRadius: '4px'
          }}
        >
          {num}
        </button>
      ))}
      {/* 다음 페이지 버튼 */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="page-button next"
        style={{ cursor: 'pointer', padding: '8px 12px' }}
      >
        &gt;
      </button>
      {/* 마지막 페이지 버튼 */}
      <button
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages}
        className="page-button"
        style={{ cursor: 'pointer', padding: '8px 12px' }}
      >
        &raquo;
      </button>
    </div>
  );
};
